import React from 'react';
import { GridColDef, GridRenderCellParams, useGridSelector } from "@mui/x-data-grid";
import { LateSwapLineup, LateSwapLineupResponse, LateSwapPlayer } from "../../lateSwap/store/types";
import { Box, Grid, IconButton, Stack } from "@mui/material";
import CheckIcon from '@material-ui/icons/Check';
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF, gridDetailPanelExpandedRowsContentCacheSelector, useGridApiContext } from '@mui/x-data-grid-pro';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import SwapHoriz from '@material-ui/icons/SwapHoriz';
import LockOutlined from '@material-ui/icons/LockOutlined';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import { getLineupUniqueHash } from '../../lateSwap/utils';
import { CustomToolTip, toolTipText } from '../CustomToolTip';
import { isInLineup } from '../../utils/showdownHandler';

interface PlayerGroup {
  id: string;
  players: LateSwapPlayer[];
  roiBoost: number;
}

// Helper function to calculate group boost for a lineup
// For Showdown, CPT is always the first player in exportableLineup, rest are FLEX
const calculateGroupBoost = (lineup: LateSwapLineup, playerGroups: PlayerGroup[]): number => {
  let totalGroupBoost = 0;
  const lineupPlayerIds = lineup.exportableLineup.split(',').map(p => p.trim());
  const cptPlayerId = lineupPlayerIds[0]; // First player is CPT
  const flexPlayerIds = lineupPlayerIds.slice(1); // Rest are FLEX

  playerGroups.forEach(group => {
    if (group.players.length > 0 && group.roiBoost !== 0) {
      // Check if ALL players in the group are in this lineup (respecting CPT/FLEX position)
      const allPlayersInLineup = group.players.every(player => {
        const playerPos = player.position?.toUpperCase();
        if (playerPos === 'CPT') {
          return player.nameAndId === cptPlayerId;
        } else if (playerPos === 'FLEX') {
          return flexPlayerIds.includes(player.nameAndId);
        }
        // Non-showdown: just check if player is in lineup
        return lineupPlayerIds.includes(player.nameAndId);
      });
      if (allPlayersInLineup) {
        totalGroupBoost += group.roiBoost;
      }
    }
  });

  return totalGroupBoost;
};

function CustomDetailPanelToggle(props: Pick<GridRenderCellParams, 'id' | 'value'>) {
  const { id, value: isExpanded } = props;
  const apiRef = useGridApiContext();

  // To avoid calling ´getDetailPanelContent` all the time, the following selector
  // gives an object with the detail panel content for each row id.
  const contentCache = useGridSelector(
    apiRef,
    gridDetailPanelExpandedRowsContentCacheSelector,
  );

  // If the value is not a valid React element, it means that the row has no detail panel.
  const hasDetail = React.isValidElement(contentCache[id]);

  return (
    <IconButton
      size="small"
      tabIndex={-1}
      disabled={!hasDetail}
      aria-label={isExpanded ? 'Close' : 'Open'}
      sx={{ color: 'white' }}
    >
      {
        isExpanded ? (
          <ExpandLessIcon />
        ) : (
          <ExpandMoreIcon />
        )
      }
    </IconButton>
  );
}

export const lateSwapLineupDataGridHeaderConfig = (savedLineups: LateSwapLineup[], lineupPositions: GridColDef[], boostedPlayers: { [key: string]: number }, handleDupeClicked: (hashcode: string, lineupNumber: string) => void, sport: string, site: string = 'DK', boostedStacks: { [key: string]: number } = {}, playerGroups: PlayerGroup[] = []): GridColDef[] => {
  const favoritedLineupHashcodes = savedLineups.map(x => getLineupUniqueHash(x));
  const dupedLineupHashcodes = savedLineups.map(x => x.lineupHashCode);
  const coldef: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      renderCell: (params) => (
        <CustomDetailPanelToggle id={params.id} value={params.value} />
      ),
    },
    {
      field: 'lineupNumber',
      headerName: 'Lineup #',
      headerAlign: 'left',
      align: 'left',
      width: 125,
      resizable: false,
      valueFormatter(params) {
        return `Lineup ${params.value}`;
      },
      renderCell: (params: GridRenderCellParams<LateSwapLineup>) => {
        const favoritedLineups = params?.row?.lateSwapOptions?.filter(x => favoritedLineupHashcodes.includes(getLineupUniqueHash(x)));
        const duped = favoritedLineups && favoritedLineups.length > 0 && dupedLineupHashcodes.filter(x => favoritedLineups[0].lineupHashCode === x).length > 1;
        const swapped = (favoritedLineups && favoritedLineups.length > 0 && !favoritedLineups[0].originalLineup);
        return (
          <Stack direction='row'>
            <Box>{`Lineup ${params.row.lineupNumber ?? 1}`}</Box>
            {swapped && (
              <Box sx={{ '& svg': { fill: 'white' }, backgroundColor: '#004803', opacity: 0.7, height: 18, width: 18, marginLeft: 4, }} borderRadius="100px" display="flex">
                <SwapHoriz style={{ color: 'white', height: 18, width: 18 }} />
              </Box>
            )}
            {duped && (
              <Box onClick={() => handleDupeClicked(favoritedLineups[0].lineupHashCode, `Lineup ${(params.row.lineupNumber ?? 1)}`)} style={{ cursor: 'pointer', backgroundColor: 'rgba(251, 255, 57, 0.5)', height: 18, width: 18, marginLeft: 4, }} borderRadius="100px" display="flex">
                <Box sx={{ color: 'white', justifyContent: 'center', alignItems: 'center', display: 'flex', fontFamily: 'sans-serif', fontWeight: 600, width: '100%' }}>
                  D
                </Box>
              </Box>
            )}
          </Stack>
        )
      },
    },
    {
      field: 'simLineupRoi',
      headerName: 'Simulated ROI',
      resizable: false,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      width: 150,
      valueGetter(params) {
        const row: LateSwapLineup = params.api.getRowParams(params.id || 0)?.row;
        const playerBoostKeys = Object.keys(boostedPlayers);
        const stackBoostKeys = Object.keys(boostedStacks);
        const boostKeys = [...playerBoostKeys, ...stackBoostKeys];
        const filteredBoostKeys = boostKeys.filter(x =>
          isInLineup(row.exportableLineup, x, site, sport) ||
          row.stacks?.find(y => ((sport.toLowerCase() === 'mlb' && y.count > 2) || (sport.toLowerCase() === 'nfl' && y.count > 0)) && y.team === x)
        );
        let boostValue = 0;
        filteredBoostKeys.forEach(x => {
          boostValue = boostValue + (boostedPlayers[x] || 0) + (boostedStacks[x] || 0);
        });

        // Add player group boosts
        const groupBoost = calculateGroupBoost(row, playerGroups);
        boostValue += groupBoost;

        if (isNaN(params.value)) {
          return null;
        }
        const percentage = ((params.value + (boostValue / 100)));
        return percentage;
      },
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
      renderCell: (params: GridRenderCellParams<LateSwapLineup>) => {
        if (isNaN(params.value)) {
          return null
        }
        const row: LateSwapLineup = params.api.getRowParams(params.id || 0)?.row;
        const playerBoostKeys = Object.keys(boostedPlayers);
        const stackBoostKeys = Object.keys(boostedStacks);
        const boostKeys = [...playerBoostKeys, ...stackBoostKeys];
        const filteredBoostKeys = boostKeys.filter(x =>
          isInLineup(row.exportableLineup, x, site, sport) ||
          row.stacks?.find(y => ((sport.toLowerCase() === 'mlb' && y.count > 2) || (sport.toLowerCase() === 'nfl' && y.count > 0)) && y.team === x)
        );
        let boostValue = 0;
        filteredBoostKeys.forEach(x => {
          boostValue = boostValue + (boostedPlayers[x] || 0) + (boostedStacks[x] || 0);
        });

        // Add player group boosts
        const groupBoost = calculateGroupBoost(row, playerGroups);
        boostValue += groupBoost;

        const percentage = (params.value * 100)?.toFixed(1);
        return (
          <Grid container justifyContent='center' style={{ fontWeight: 700 }}>
            <Grid item>
              {percentage}%
            </Grid>
            {boostValue !== 0 && (
              <Grid item style={{
                display: 'flex',
                color: boostValue > 0 ? '#00BD08' : '#FF2525',
              }} alignItems='center'>
                {boostValue > 0 ? <ArrowUpwardIcon style={{ color: 'inherit' }} fontSize='small' /> : <ArrowDownwardIcon style={{ color: 'inherit' }} fontSize='small' />}
                {boostValue}%
              </Grid>
            )}
          </Grid>
        )
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.simulatedROI}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'simAverageFantasyPoints',
      headerName: 'Sim Avg Points',
      type: 'number',
      headerAlign: 'left',
      resizable: false,
      align: 'left',
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const trim = params.value?.toFixed(2);
        return trim;
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.simAvgPoints}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'ownershipSum',
      headerName: 'Own Proj',
      type: 'number',
      headerAlign: 'left',
      resizable: false,
      align: 'left',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.ownProj}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'salary',
      headerName: 'Salary',
      type: 'number',
      headerAlign: 'left',
      resizable: false,
      align: 'left',
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        return '$' + params.value;
      },
    },
    {
      field: 'stacks',
      headerName: 'Stack',
      flex: .1,
      headerAlign: 'center',
      align: 'center',
      valueGetter(value,) {
        const teams = value.row.stacks?.map(x => x.team);
        if (sport?.toUpperCase() === 'NFL' && teams && teams.length > 0) {
          return teams.join('/')
        }
        return teams.join();
      },
      valueFormatter(params) {
        return params.value || '-';
      },
      renderCell(params) {
        return (
          <Box fontWeight={700} fontSize='14px'>
            {params.formattedValue}
          </Box>
        )
      },
    },
    {
      field: 'simLineupRoiPct',
      headerName: 'Stack Type',
      flex: .1,
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      valueGetter(value,) {

        if (sport?.toUpperCase() === 'NFL') {
          const counts = value.row.stacks?.map(x => ({ count: x.count, rbCount: x.runbackCount }));
          if (counts && counts.length > 0) {
            return counts.map(x => x.rbCount > 0 ? `QB + ${x.count} | ${x.rbCount} OPP` : `QB + ${x.count}`).join("/")
          }
        }
        const counts2 = value.row.stacks?.map(x => x.count);
        return counts2?.join('-') || '-';
      },
      renderCell: (params: GridRenderCellParams<LateSwapLineup>) => {
        const row: LateSwapLineup = params.api.getRowParams(params.id || 0)?.row;
        const teams = row.stacks.map(x => x.count);
        if (sport?.toUpperCase() === 'NFL' && teams && teams.length > 0) {
          return (
            <Grid container justifyContent='center'>
              {params.value}
            </Grid>
          );
        }
        return (
          <Grid container justifyContent='center' style={{ fontWeight: 700, fontSize: '14px', }}>
            {teams?.join('-') || '-'}
          </Grid>
        )
      },
    },
    ...lineupPositions,
    {
      field: 'fullLineup',
      resizable: false,
      headerName: 'Favorite',
      renderCell: (params: GridRenderCellParams<LateSwapLineup>) => {
        const swapLineupLineupHashCodes = params?.row?.lateSwapOptions?.map(x => getLineupUniqueHash(x))
        return (
          <Grid container justifyContent="space-between" style={{ padding: 2, fontSize: '16px', }}>
            <Grid container item xs={12} alignItems='center' justifyContent='center'>
              {
                savedLineups.some((x => swapLineupLineupHashCodes?.includes(getLineupUniqueHash(x)))) ? (
                  <Box style={{ backgroundColor: '#009C06' }} borderRadius="100px" display="flex">
                    <CheckIcon />
                  </Box>
                ) : (
                  <Box style={{ backgroundColor: 'grey', opacity: 0.7 }} borderRadius="100px" display="flex">
                    <CheckIcon />
                  </Box>
                )
              }
            </Grid>
          </Grid>
        )
      }
    },
  ];

  return coldef;
}

export const lateSwapFavoritesDataGridHeaderConfig = (lineupPositions: GridColDef[], handleDupeClicked: (hashcode: string, lineupNumber: string) => void): GridColDef[] => {

  const coldef: GridColDef[] = [
    {
      field: 'lineupNumber',
      headerName: 'Lineup #',
      headerAlign: 'left',
      align: 'left',
      width: 110,
      resizable: false,
      valueFormatter(params) {
        return `Lineup ${params.value}`;
      },
      renderCell: (params: GridRenderCellParams<LateSwapLineup>) => {
        return (
          <Box>{`Lineup ${(params.row.lineupNumber ?? 1)}`}</Box>
        )
      },
    },
    {
      field: 'simLineupRoi',
      headerName: 'Simulated ROI',
      resizable: false,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
      renderCell: (params: GridRenderCellParams<LateSwapLineup>) => {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return (
          <Grid container justifyContent='space-between'>
            <Grid item>
              {percentage}%
            </Grid>
          </Grid>
        )
      },
    },
    {
      field: 'simAverageFantasyPoints',
      headerName: 'Sim Avg Points',
      type: 'number',
      headerAlign: 'left',
      resizable: false,
      align: 'left',
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const trim = params.value?.toFixed(2);
        return trim;
      },
    },
    {
      field: 'ownershipSum',
      headerName: 'Own Proj',
      type: 'number',
      headerAlign: 'left',
      resizable: false,
      align: 'left',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
    },
    {
      field: 'salary',
      headerName: 'Salary',
      type: 'number',
      headerAlign: 'left',
      resizable: false,
      align: 'left',
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        return '$' + params.value;
      },
    },
    ...lineupPositions,
    {
      field: 'originalLineup',
      resizable: false,
      headerName: 'Swapped',
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params: GridRenderCellParams<LateSwapLineup>) => params.row.originalLineup ? '-' : 'Yes',
      renderCell: (params: GridRenderCellParams<LateSwapLineup>) => {
        const text = params.row.originalLineup ? '-' : ''
        return (
          <Stack direction='row'>
            <Box>{text}</Box>
            {!params.row.originalLineup && (
              <Box style={{ backgroundColor: '#004803', opacity: 0.7, height: 18, width: 18, marginLeft: 4, }} borderRadius="100px" display="flex">
                <SwapHoriz style={{ height: 18, width: 18 }} />
              </Box>
            )}
          </Stack>
        )
      },
    },
    {
      field: 'dupes',
      resizable: false,
      headerName: 'Dupes',
      headerAlign: 'left',
      type: 'number',
      valueGetter: (params: GridRenderCellParams<LateSwapLineup>) => params.row.dupes ?? 0,
      renderCell: (params: GridRenderCellParams<LateSwapLineup>) => {

        return (
          <Stack direction='row'>
            <Box>{params.row.dupes}</Box>
            {(params.row.dupes && params.row.dupes > 0) ? (
              <Box onClick={() => handleDupeClicked(params.row.lineupHashCode, `Lineup ${(params.row.lineupNumber ?? 1)}`)} style={{ cursor: 'pointer', backgroundColor: 'rgba(251, 255, 57, 0.5)', height: 18, width: 18, marginLeft: 4, }} borderRadius="100px" display="flex">
                <Box style={{ color: 'white', justifyContent: 'center', alignItems: 'center', display: 'flex', fontFamily: 'sans-serif', fontWeight: 600, width: '100%' }}>
                  D
                </Box>
              </Box>
            ) : ''}
          </Stack>
        )
      },
    },
  ];

  return coldef;
}

export const lateSwapExposuresDataGridHeaderConfig = (): GridColDef<LateSwapPlayer>[] => {
  const headerConfig: GridColDef<LateSwapPlayer>[] = [
    {
      field: 'name',
      headerName: 'Player',
      flex: .2,
      headerAlign: 'left',
    },
    {
      field: 'team',
      headerName: 'Team',
      renderCell: (params: GridRenderCellParams<LateSwapPlayer>) => {
        return (
          <Stack direction='row'>
            <Box>{params.row.team}</Box>
            <Box display="flex" height="20px" width="20px" marginRight="4px" marginLeft="4px">
              <img src={params.row.teamLogo} height="100%" width="100%" style={{ objectFit: 'contain' }} alt="team" />
            </Box>
          </Stack>
        )
      },
      flex: .12,
      headerAlign: 'left',
    },
    {
      field: 'position',
      headerName: 'Position',
      flex: .16,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'salary',
      headerName: 'Salary',
      flex: .16,
      headerAlign: 'left',
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        return `$${params.value}`;
      },
    },
    {
      field: 'projection',
      headerName: 'Projection',
      flex: .16,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const trim = params.value?.toFixed(2);
        return trim;
      },
    },
    {
      field: 'originalExposure',
      headerName: 'Pre Swap Exposure',
      flex: .16,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const trim = (params.value * 100)?.toFixed(1);
        return `${trim}%`;
      },
    },
    {
      field: 'postSwapExposure',
      headerName: 'Post Swap Exposure',
      flex: .16,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return '0.0%'
        }
        const percentage = (params.value)?.toFixed(1);
        return percentage + '%';
      },
    },
    {
      field: 'exposureChange',
      headerName: 'Exposure Change',
      flex: .16,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueParser(value, params) {
        return (params?.row.postSwapExposure ?? 0) - ((params?.row.originalExposure ?? 0) * 100);
      },
      valueGetter: (params: GridRenderCellParams<LateSwapPlayer>) => (params?.row.postSwapExposure ?? 0) - ((params?.row.originalExposure ?? 0) * 100),
      renderCell: (params: GridRenderCellParams<LateSwapPlayer>) => {
        const difference = (params?.row.postSwapExposure ?? 0) - ((params?.row.originalExposure ?? 0) * 100);
        return (
          <Box>{difference.toFixed(1)}%</Box>
        )
      },
    },
    {
      field: 'ownership',
      headerName: 'Projected Ownership',
      flex: .16,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
    },
    {
      field: 'leverage',
      headerName: 'Leverage',
      flex: .16,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params: GridRenderCellParams<LateSwapPlayer>) => {
        return (params?.row.postSwapExposure ?? 0) - ((params?.row.ownership ?? 0) * 100)
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value)?.toFixed(1);
        return percentage + '%';
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.leverage}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'locked',
      resizable: false,
      headerName: 'Locked',
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => params.row.locked ? 'Yes' : '-',
      renderCell: (params: GridRenderCellParams<LateSwapPlayer>) => {
        const text = params.row.locked ? '' : '-'
        return (
          <Stack direction='row'>
            <Box>{text}</Box>
            {params.row.locked && (
              <Box style={{ opacity: 0.7, height: 18, width: 18, marginLeft: 4, }} borderRadius="100px" display="flex">
                <LockOutlined style={{ height: 18, width: 18, color: 'red' }} />
              </Box>
            )}
          </Stack>
        )
      },
    },
  ];
  return headerConfig;
};

export const lateSwapStackExposuresDataGridHeaderConfig = (nfl: boolean, lineups: LateSwapLineupResponse): GridColDef[] => {
  const qbNameColDef: GridColDef = {
    field: 'qbName',
    headerName: 'Quarterback',
    flex: 0.2,
    headerAlign: 'center',
    align: 'center',
  };
  const teamColDef: GridColDef = {
    field: 'team',
    headerName: 'Team',
    flex: 0.2,
    headerAlign: 'center',
    align: 'center',
  };
  const opponentTeamNameColDef: GridColDef = {
    field: 'opponentTeamName',
    headerName: 'Opponent',
    flex: 0.2,
    headerAlign: 'center',
    align: 'center',
  };
  const threeMLBOneNFLStackExposurePctColDef: GridColDef = {
    field: 'ThreeMLBOneNFLStackExposurePct',
    headerName: nfl ? 'QB + 1 WR/TE/RB' : 'Post Swap 3 Stack Exposures',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };
  const preSwapThreeMLBOneNFLStackExposurePctColDef: GridColDef = {
    field: 'PreSwapThreeMLBOneNFLStackExposurePct',
    headerName: nfl ? 'Pre Swap QB + 1 WR/TE/RB' : 'Pre Swap 3 Stack Exposures',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };
  const threePoolOwnColDef: GridColDef = {
    field: '3PoolOwn',
    headerName: '3 Stack Pool Own',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const stackCount = lineups?.lateSwapData?.filter(x => x?.stacks?.find(y => y.count === 3 && y.team === params.row.team))?.length;
      return stackCount / lineups?.lateSwapData?.length;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };
  const threePoolLevColDef: GridColDef = {
    field: '3PoolLev',
    headerName: '3 Stack Pool Leverage',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const stackCount = lineups?.lateSwapData?.filter(x => x?.stacks?.find(y => y.count === 3 && y.team === params.row.team))?.length;
      return params.row.ThreeMLBOneNFLStackExposurePct - (stackCount / lineups?.lateSwapData?.length);
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };
  const fourMLBTwoNFLStackExposurePctColDef: GridColDef = {
    field: 'FourMLBTwoNFLStackExposurePct',
    headerName: nfl ? 'QB + 2 WR/TE/RB' : 'Post Swap 4 Stack Exposures',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };
  const preSwapFourMLBTwoNFLStackExposurePctColDef: GridColDef = {
    field: 'PreSwapFourMLBTwoNFLStackExposurePct',
    headerName: nfl ? 'Pre Swap QB + 2 WR/TE/RB' : 'Pre Swap 4 Stack Exposures',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };
  const fourPoolOwnColDef: GridColDef = {
    field: '4PoolOwn',
    headerName: '4 Stack Pool Own',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const stackCount = lineups?.lateSwapData?.filter(x => x?.stacks?.find(y => y.count === 4 && y.team === params.row.team))?.length;
      return stackCount / lineups?.lateSwapData?.length;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };
  const fourPoolLevColDef: GridColDef = {
    field: '4PoolLev',
    headerName: '4 Stack Pool Leverage',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const stackCount = lineups?.lateSwapData?.filter(x => x?.stacks?.find(y => y.count === 4 && y.team === params.row.team))?.length;
      return params.row.FourMLBTwoNFLStackExposurePct - (stackCount / lineups?.lateSwapData?.length);
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };
  const fiveMLBThreeNFLStackExposurePctColDef: GridColDef = {
    field: 'FiveMLBThreeNFLStackExposurePct',
    headerName: nfl ? 'QB + 3 WR/TE/RB' : 'Post Swap 5 Stack Exposures',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };
  const preSwapFiveMLBThreeNFLStackExposurePctColDef: GridColDef = {
    field: 'PreSwapFiveMLBThreeNFLStackExposurePct',
    headerName: nfl ? 'Pre Swap QB + 3 WR/TE/RB' : 'Pre Swap 5 Stack Exposures',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return '-';
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };
  const fivePoolOwnColDef: GridColDef = {
    field: '5PoolOwn',
    headerName: '5 Stack Pool Own',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const stackCount = lineups?.lateSwapData?.filter(x => x?.stacks?.find(y => y.count === 5 && y.team === params.row.team))?.length;
      return stackCount / lineups?.lateSwapData?.length;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };
  const fivePoolLevColDef: GridColDef = {
    field: '5PoolLev',
    headerName: '5 Stack Pool Leverage',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const stackCount = lineups?.lateSwapData?.filter(x => x?.stacks?.find(y => y.count === 5 && y.team === params.row.team))?.length;
      return params.row.FiveMLBThreeNFLStackExposurePct - (stackCount / lineups?.lateSwapData?.length);
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };
  
  const threePoolStackExposureDiff: GridColDef = {
    field: '3PoolDiff',
    headerName: '3 Stack Exposure Change',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const preSwapExposure = params.row.PreSwapThreeMLBOneNFLStackExposurePct || 0;
      const postSwapExposure = params.row.ThreeMLBOneNFLStackExposurePct || 0;
      return postSwapExposure - preSwapExposure;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };
  
  const fourPoolStackExposureDiff: GridColDef = {
    field: '4PoolDiff',
    headerName: '4 Stack Exposure Change',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const preSwapExposure = params.row.PreSwapFourMLBTwoNFLStackExposurePct || 0;
      const postSwapExposure = params.row.FourMLBTwoNFLStackExposurePct || 0;
      return postSwapExposure - preSwapExposure;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };
  
  const fivePoolStackExposureDiff: GridColDef = {
    field: '5PoolDiff',
    headerName: '5 Stack Exposure Change',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const preSwapExposure = params.row.PreSwapFiveMLBThreeNFLStackExposurePct || 0;
      const postSwapExposure = params.row.FiveMLBThreeNFLStackExposurePct || 0;
      return postSwapExposure - preSwapExposure;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };

  const headerConfig: GridColDef[] = [
    teamColDef,
    // opponentTeamNameColDef,
    preSwapThreeMLBOneNFLStackExposurePctColDef,
    threeMLBOneNFLStackExposurePctColDef,
    threePoolStackExposureDiff,
    // threePoolOwnColDef,
    // threePoolLevColDef,
    preSwapFourMLBTwoNFLStackExposurePctColDef,
    fourMLBTwoNFLStackExposurePctColDef,
    fourPoolStackExposureDiff,
    // fourPoolOwnColDef,
    // fourPoolLevColDef,
    preSwapFiveMLBThreeNFLStackExposurePctColDef,
    fiveMLBThreeNFLStackExposurePctColDef,
    fivePoolStackExposureDiff,    
    // fivePoolOwnColDef,
    // fivePoolLevColDef,
  ];

  if (nfl) {
    return [qbNameColDef, teamColDef, threeMLBOneNFLStackExposurePctColDef, fourMLBTwoNFLStackExposurePctColDef, fiveMLBThreeNFLStackExposurePctColDef];
  }
  return headerConfig;
}